import React, { useState, useCallback, memo } from 'react';
import { useComunicacao } from '../../contexts/ComunicacaoContext';
import { useAuth } from '../../contexts/AuthContext';
import { Canal } from '../../types/comunicacao';
import ModalCriarCanal from './ModalCriarCanal';
import ModalEditarCanal from './ModalEditarCanal';

const IconeGeral = () => (
  <svg className="w-5 h-5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
  </svg>
);

const IconeDepartamento = () => (
  <svg className="w-5 h-5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);

const IconePersonalizado = () => (
  <svg className="w-5 h-5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0" />
  </svg>
);

interface ItemCanalProps {
  canal: Canal;
  selecionado: boolean;
  isAdmin: boolean;
  usuarioId?: string;
  onSelecionar: (id: string) => void;
  onEditar: (id: string, e: React.MouseEvent) => void;
}

const ItemCanal = memo(({ canal, selecionado, isAdmin, usuarioId, onSelecionar, onEditar }: ItemCanalProps) => {
  const podeEditar = canal.tipo === 'personalizado' && (isAdmin || canal.criado_por === usuarioId);

  return (
    <div
      onClick={() => onSelecionar(canal.id)}
      className={`px-4 py-3 cursor-pointer flex items-center justify-between ${
        selecionado ? 'bg-gray-600' : 'hover:bg-gray-700'
      }`}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {canal.tipo === 'geral' ? <IconeGeral /> : canal.tipo === 'departamento' ? <IconeDepartamento /> : <IconePersonalizado />}
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate text-sm">{canal.nome}</div>
          <div className="text-xs text-gray-400">
            {canal.total_topicos ?? 0} {canal.total_topicos === 1 ? 'tópico' : 'tópicos'}
          </div>
        </div>
      </div>

      {podeEditar && (
        <button
          onClick={(e) => onEditar(canal.id, e)}
          className="ml-2 p-1 rounded hover:bg-gray-500 opacity-0 group-hover:opacity-100 flex-shrink-0"
          title="Editar canal"
        >
          <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
      )}
    </div>
  );
});

const ListaCanais: React.FC = () => {
  const { canais, canalSelecionado, selecionarCanal } = useComunicacao();
  const { usuario } = useAuth();
  const [modalCriarAberto, setModalCriarAberto] = useState(false);
  const [modalEditarAberto, setModalEditarAberto] = useState(false);
  const [canalParaEditar, setCanalParaEditar] = useState<string | null>(null);

  const isAdmin = usuario?.tipo === 'admin';

  const handleEditar = useCallback((canalId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setCanalParaEditar(canalId);
    setModalEditarAberto(true);
  }, []);

  return (
    <>
      <div className="w-64 bg-gray-800 text-white flex flex-col h-full flex-shrink-0">
        <div className="p-4 border-b border-gray-700 flex-shrink-0">
          <h2 className="text-lg font-bold">Comunicação</h2>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain">
          {canais.map((canal) => (
            <ItemCanal
              key={canal.id}
              canal={canal}
              selecionado={canalSelecionado?.id === canal.id}
              isAdmin={isAdmin}
              usuarioId={usuario?.id}
              onSelecionar={selecionarCanal}
              onEditar={handleEditar}
            />
          ))}
        </div>

        {isAdmin && (
          <div className="p-4 border-t border-gray-700 flex-shrink-0">
            <button
              onClick={() => setModalCriarAberto(true)}
              className="w-full px-4 py-2 bg-bravo-primary hover:bg-red-700 text-white rounded-md flex items-center justify-center gap-2 text-sm font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Criar Canal
            </button>
          </div>
        )}
      </div>

      {modalCriarAberto && (
        <ModalCriarCanal onClose={() => setModalCriarAberto(false)} />
      )}

      {modalEditarAberto && canalParaEditar && (
        <ModalEditarCanal
          canalId={canalParaEditar}
          onClose={() => {
            setModalEditarAberto(false);
            setCanalParaEditar(null);
          }}
        />
      )}
    </>
  );
};

export default ListaCanais;
